import { useTabs } from "@/contexts/tabsContext";
import { NewspaperIcon } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { latestVersion, setLatestVersion } from "../changelog/backlog";
import { ChangeLogTabContent } from "../dynamicImports";
import { TabData } from "rc-dock";
import { useService } from "@/hooks/useService";
import { RPC_TIER_GLOBAL } from "@/constants/rpcPriorities";
import {
  RELEASE_SERVICE_IDENT,
  type IReleaseServicePublic,
} from "@bloomstack/apis/services/IReleaseServicePublic";

/** Changelog tab wrapper - sets tab title and renders content */
export const ChangeLogTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs(false);
  const releaseService = useService<IReleaseServicePublic>(RELEASE_SERVICE_IDENT)
    .withPriority("normal", () => RPC_TIER_GLOBAL);
  const [currentVersion, setCurrentVersion] = useState(latestVersion);

  useEffect(() => {
    releaseService.getLatestVersion().then((result) => {
      if (result.success) {
        setLatestVersion(result.result);
        setCurrentVersion(result.result);
      }
    });
  }, []);

  useEffect(() => {
    tabs?.setTabMetadata(tabId, {
      title: currentVersion !== "0" ? `What's New (v${currentVersion})` : "What's New",
      icon: <NewspaperIcon className="w-4 h-4" />
    });
  }, [tabId, tabs, currentVersion]);

  return <ChangeLogTabContent />;
};

const ChangeLogTabTitle: FC = () => {
  const [version, setVersion] = useState(latestVersion);
  const releaseService = useService<IReleaseServicePublic>(RELEASE_SERVICE_IDENT)
    .withPriority("normal", () => RPC_TIER_GLOBAL);

  useEffect(() => {
    if (latestVersion !== "0") {
      setVersion(latestVersion);
      return;
    }
    releaseService.getLatestVersion().then((result) => {
      if (result.success) {
        setLatestVersion(result.result);
        setVersion(result.result);
      }
    });
  }, []);

  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      {version !== "0" ? `What's New (v${version})` : "What's New"}
    </div>
  );
};

export const ChangeLogTabRenderer = (id: string): TabData => {
  return {
    id,
    title: <ChangeLogTabTitle />,
    content: () => <ChangeLogTab tabId={id} />
  }
}